/**
 * VsCrystalKnowsPage — the public /vs/crystal-knows competitor comparison page.
 *
 * Ported verbatim from the locked design
 * design/mockups/V1_Landing_VS_Crystal_Knows_v1.html (Template 2 competitor
 * variant, source V1_Competitor_Comparison_Pages.md Page 1). Replaces the
 * noindex marketing scaffold for /vs/crystal-knows with real, indexable
 * content.
 *
 * COMPETITOR CLAIMS ARE FACT-CHECK-GATED PRE-PUBLISH: every cell in the
 * Crystal Knows column of the comparison table — and the competitor claims in
 * the hero, definition, fair-block, and FAQ — must be fact-checked against
 * Crystal Knows' current site before publish; Crystal's feature set and
 * pricing change. Legal cleared the competitor pages to mock (Russ, 2026-05-20);
 * the "personality-type framework" wording is factual reporting about a
 * competitor, not adoption of the vocabulary as PeoplePrints' own (scoped
 * exception per the landing-system spec).
 *
 * The app shell's <TopNav> renders the nav; this page is content + footer
 * (everything below the mockup's <nav class="marketing-nav">). Scoped <style>
 * block aliases the mockup's colors onto locked --ppr-* tokens; replicates the
 * locked pattern from PricingPage.jsx.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const VSCRYSTAL_DESCRIPTION =
  "Crystal Knows reads people from their public profile, built for sales. PeoplePrints reads people from minimal common data points, built for HR. Here's the honest comparison.";

// competitor claims are fact-check-gated pre-publish — verify every Crystal
// Knows cell against Crystal's current site at publish time.
const COMPARISON_ROWS = [
  {
    feature: "Built for",
    pp: "HR — interviews, reviews, mediation, offers",
    rival: "Sales — pre-meeting communication",
  },
  {
    feature: "Input required",
    pp: "Minimal common data points you already have",
    rival: "Primarily public profile data",
  },
  {
    feature: "Works without a LinkedIn footprint",
    pp: "Yes",
    rival: "Limited — the read depends on discoverable public data",
  },
  {
    feature: "What the user sees",
    pp: "A tactical Blueprint in plain HR language",
    rival: "A read based on a personality-type framework",
  },
  {
    feature: "HR conversation Blueprints",
    pp: "Interview, performance review, PIP, exit, and more",
    rival: "Not an HR-specific feature set",
  },
  {
    feature: "Conflict mediation surface",
    pp: "Yes — a first-class V1 feature",
    rival: "No",
  },
  {
    feature: "Claim register",
    pp: 'Hedge-disciplined ("tends to," "watch for")',
    rival: "More declarative",
  },
  {
    feature: "Browser extension",
    pp: "Shipping in V1.x",
    rival: "Yes — an established strength",
  },
  {
    feature: "Starting price",
    pp: "$49/month",
    rival: "Subscription; tiers vary — check current pricing",
  },
];

const FAQS = [
  {
    q: "Is PeoplePrints a direct replacement for Crystal Knows?",
    a: "For an HR use case, yes. For a sales use case, Crystal Knows was purpose-built for that and is the more natural fit. PeoplePrints is the better choice when the conversations are interviews, performance reviews, conflict mediations, and offers.",
  },
  {
    q: "Can PeoplePrints read someone who has no LinkedIn profile?",
    a: "Yes. PeoplePrints generates a Predisposition Profile from minimal common data points, so it produces a read regardless of the person's public footprint. Tools that depend on a public-profile scrape cannot.",
  },
  {
    q: "Does PeoplePrints show a personality type the way Crystal does?",
    a: "No. PeoplePrints does not surface a personality-type label. It produces a tactical Blueprint in plain HR language — the guidance for the conversation, not a category the user has to learn.",
  },
  {
    q: "Which is cheaper?",
    a: "PeoplePrints starts at $49/month. Crystal Knows pricing varies by plan — check their current pricing. Compare on fit for the use case first, then on price.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function VsCrystalKnowsPage() {
  usePageMeta({
    title:
      "PeoplePrints vs. Crystal Knows — A Crystal Knows Alternative Built for HR",
    description: VSCRYSTAL_DESCRIPTION,
    canonicalPath: "/vs/crystal-knows",
  });

  return (
    <div className="ppr-vscrystal-root">
      <style>{VSCRYSTAL_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-vscrystal-hero">
        <div className="ppr-vscrystal-hero-inner">
          <p className="ppr-vscrystal-hero-eyebrow">
            PeoplePrints vs. Crystal Knows
          </p>
          <h1 className="ppr-vscrystal-hero-h1">
            Looking for a Crystal Knows alternative built for HR, not sales?
          </h1>
          <p className="ppr-vscrystal-hero-sub">
            Crystal Knows is a capable tool. It was built for sales
            professionals preparing for a meeting, and it reads people from
            their public profile. PeoplePrints was built for HR — interviews,
            performance reviews, conflict mediation, offers — and it reads
            people from minimal common data points you already have, with no
            public footprint required. Here is the honest comparison.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-vscrystal-block ppr-vscrystal-compact">
        <div className="ppr-vscrystal-block-inner">
          <div className="ppr-vscrystal-definition">
            <p className="ppr-vscrystal-def-label">
              PeoplePrints as a Crystal Knows alternative
            </p>
            <p className="ppr-vscrystal-def-text">
              PeoplePrints is a pre-interaction intelligence tool built for HR.
              It is an alternative to Crystal Knows for users whose use case is
              HR rather than sales. The core difference: Crystal Knows
              generates its read primarily from a person's public profile data
              and is oriented toward sales communication; PeoplePrints
              generates a <strong>Predisposition Profile</strong> from minimal
              common data points — no public footprint required — and produces
              HR-specific Blueprints for interviews, performance reviews,
              conflict mediation, and offers.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE HONEST COMPARISON ── */}
      <section className="ppr-vscrystal-block ppr-vscrystal-compact">
        <div className="ppr-vscrystal-block-inner">
          <p className="ppr-vscrystal-block-eyebrow">The honest comparison</p>
          <h2 className="ppr-vscrystal-block-h2">
            When PeoplePrints is the better fit — and when Crystal Knows is.
          </h2>
          <p className="ppr-vscrystal-block-body">
            Crystal Knows pioneered the pre-meeting personality read and does
            its core job well. If your use case is a salesperson preparing for
            a prospect call and the prospect has a developed LinkedIn presence,
            Crystal Knows is a reasonable tool.
          </p>
          <p className="ppr-vscrystal-block-body">
            PeoplePrints is the better fit when the use case is HR. Three
            reasons: HR-relevant people frequently do not have the public
            profile footprint Crystal's read depends on — senior candidates,
            operational staff, and employees in conflict often have minimal
            public presence. HR conversations are not sales conversations —
            they are interviews, reviews, mediations, and offers, and
            PeoplePrints generates a Blueprint specific to each. And
            PeoplePrints includes a Conflict Mediation surface, which a
            sales-oriented tool has no reason to build.
          </p>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="ppr-vscrystal-block ppr-vscrystal-compact ppr-vscrystal-tinted">
        <div className="ppr-vscrystal-block-inner">
          <p className="ppr-vscrystal-block-eyebrow">Side by side</p>
          <h2 className="ppr-vscrystal-block-h2">
            PeoplePrints vs. Crystal Knows.
          </h2>
          {/* competitor claims are fact-check-gated pre-publish */}
          <table className="ppr-vscrystal-cmp-table">
            <thead>
              <tr>
                <th></th>
                <th className="ppr-vscrystal-col-pp">PeoplePrints</th>
                <th className="ppr-vscrystal-col-rival">Crystal Knows</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.feature}>
                  <th>{row.feature}</th>
                  <td className="ppr-vscrystal-col-pp">{row.pp}</td>
                  <td className="ppr-vscrystal-col-rival">{row.rival}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ppr-vscrystal-cmp-note">
            FACT-CHECK before publish: every cell in the Crystal Knows column.
            Crystal's feature set and pricing change; verify against Crystal's
            current site at publish time.
          </p>
        </div>
      </section>

      {/* ── WHERE THE COMPETITOR IS AHEAD ── */}
      <section className="ppr-vscrystal-block ppr-vscrystal-compact">
        <div className="ppr-vscrystal-block-inner">
          {/* competitor claims are fact-check-gated pre-publish */}
          <div className="ppr-vscrystal-fair-block">
            <p className="ppr-vscrystal-fb-label">
              Where Crystal Knows is ahead
            </p>
            <p className="ppr-vscrystal-fb-body">
              Honesty matters here. Crystal Knows has a more mature browser
              extension than PeoplePrints' V1 — Crystal's extension is an
              established part of the product, and PeoplePrints' ships in V1.x.
              And if your use case genuinely is sales rather than HR, Crystal
              was built for that and PeoplePrints was not.{" "}
              <strong>
                We would rather tell you that than win a customer who needed
                the other tool.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-vscrystal-block ppr-vscrystal-compact ppr-vscrystal-tinted">
        <div className="ppr-vscrystal-block-inner">
          <p className="ppr-vscrystal-block-eyebrow">FAQ</p>
          <h2 className="ppr-vscrystal-block-h2">
            PeoplePrints vs. Crystal Knows — common questions.
          </h2>
          <div className="ppr-vscrystal-faq-list">
            {FAQS.map((f, i) => (
              <details
                className="ppr-vscrystal-faq-item"
                key={f.q}
                open={i === 0}
              >
                <summary className="ppr-vscrystal-faq-q">{f.q}</summary>
                <p className="ppr-vscrystal-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-vscrystal-cred-strip">
        <div className="ppr-vscrystal-cred-strip-inner">
          <span className="ppr-vscrystal-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-vscrystal-cta-band">
        <div className="ppr-vscrystal-cta-band-inner">
          <h2 className="ppr-vscrystal-cta-band-h">
            A Crystal Knows alternative built for the HR conversation.
          </h2>
          <div className="ppr-vscrystal-cta-actions">
            <Link
              to="/demo"
              className="ppr-vscrystal-cta-btn ppr-vscrystal-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/for/hr-directors"
              className="ppr-vscrystal-cta-btn ppr-vscrystal-cta-secondary"
            >
              See PeoplePrints for HR Directors
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer — clean A6 nav ── */}
      <footer className="ppr-vscrystal-footer">
        <div className="ppr-vscrystal-footer-inner">
          <div className="ppr-vscrystal-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-vscrystal-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-vscrystal-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const VSCRYSTAL_CSS = `
.ppr-vscrystal-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design (same flag as PricingPage / the C1 homepage rebuild).
     gold-soft / gold-deep (fair-block) likewise have no token — derived
     literals, flagged for design alongside --c-gold. */
  --c-gold: #C99A4B;
  --c-gold-soft: #FBF6EC;
  --c-gold-deep: #8A6926;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-vscrystal-root * { box-sizing: border-box; }

.ppr-vscrystal-block { padding: 64px 32px; }
.ppr-vscrystal-block.ppr-vscrystal-compact { padding: 44px 32px; }
.ppr-vscrystal-block.ppr-vscrystal-tinted { background: var(--c-pale-soft); }
.ppr-vscrystal-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-vscrystal-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-vscrystal-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-vscrystal-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-vscrystal-block-body strong { color: var(--c-navy); }

.ppr-vscrystal-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-vscrystal-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-vscrystal-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-vscrystal-hero-h1 { font-family: var(--f-display); font-size: 38px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-vscrystal-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-vscrystal-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-vscrystal-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-vscrystal-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-vscrystal-def-text strong { color: var(--c-navy); }

.ppr-vscrystal-cmp-table { width: 100%; border-collapse: collapse; margin: 20px 0 0; font-family: var(--f-ui); font-size: 13.5px; }
.ppr-vscrystal-cmp-table th, .ppr-vscrystal-cmp-table td { text-align: left; padding: 12px 16px; border-bottom: 1px solid var(--c-border); vertical-align: top; }
.ppr-vscrystal-cmp-table thead th { font-family: var(--f-display); font-size: 12px; letter-spacing: 0.04em; }
.ppr-vscrystal-cmp-table thead th.ppr-vscrystal-col-pp { color: var(--c-coral-dark); border-bottom: 2px solid var(--c-coral); }
.ppr-vscrystal-cmp-table thead th.ppr-vscrystal-col-rival { color: var(--c-steel-dark); border-bottom: 2px solid var(--c-border); }
.ppr-vscrystal-cmp-table tbody th { font-family: var(--f-ui); font-weight: 700; color: var(--c-navy); width: 30%; }
.ppr-vscrystal-cmp-table td.ppr-vscrystal-col-pp { color: var(--c-text); background: var(--c-coral-very-soft); }
.ppr-vscrystal-cmp-table td.ppr-vscrystal-col-rival { color: var(--c-text-2); }
.ppr-vscrystal-cmp-note { font-family: var(--f-mono); font-size: 10.5px; color: var(--c-muted); margin: 12px 0 0; line-height: 1.5; }

.ppr-vscrystal-fair-block { background: var(--c-gold-soft); border: 1.5px solid var(--c-gold); padding: 22px 26px; }
.ppr-vscrystal-fb-label { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.10em; text-transform: uppercase; color: var(--c-gold-deep); margin: 0 0 8px; }
.ppr-vscrystal-fb-body { font-family: var(--f-body); font-size: 15.5px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-vscrystal-fb-body strong { color: var(--c-navy); }

.ppr-vscrystal-faq-list { margin: 20px 0 0; }
.ppr-vscrystal-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-vscrystal-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; cursor: pointer; list-style: none; }
.ppr-vscrystal-faq-q::-webkit-details-marker { display: none; }
.ppr-vscrystal-faq-item[open] .ppr-vscrystal-faq-q { margin-bottom: 8px; }
.ppr-vscrystal-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-vscrystal-faq-a strong { color: var(--c-navy); }

.ppr-vscrystal-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-vscrystal-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-vscrystal-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-vscrystal-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-vscrystal-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-vscrystal-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-vscrystal-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-vscrystal-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-vscrystal-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-vscrystal-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-vscrystal-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-vscrystal-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-vscrystal-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-vscrystal-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-vscrystal-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-vscrystal-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-vscrystal-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-vscrystal-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-vscrystal-hero-h1 { font-size: 28px; }
  .ppr-vscrystal-block-h2 { font-size: 22px; }
  .ppr-vscrystal-cmp-table { font-size: 12px; }
  .ppr-vscrystal-cmp-table th, .ppr-vscrystal-cmp-table td { padding: 9px 10px; }
}
`;
